import cx from "classnames";
import { faInfoCircle, faQuestionCircle, faQuoteLeft } from "@fortawesome/pro-regular-svg-icons";

import LinkButton from "common/components/LinkButton";
import { OrderModificationOrderItem } from "common/types/orders";
import { getSummaryItems } from "common/utils/summary";
import { PriceSummaryItemKind } from "common/types/priceSummary";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import Modal from "client/common/components/deprecated-modals/Modal";
import Tooltip from "client/common/components/content/Tooltip";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import { urls } from "client/routes";
import Box from "client/common/components/layouts/Box";
import { sendSelectItem } from "client/analytics/utils";

import { useOrderModificationsQuery } from "../hooks";
import { SERVICE_FEE_HELP_TEXT } from "../constants";

import styles from "../styles/OrderModificationsModal.module.less";

const renderPriceLabel = (kind: PriceSummaryItemKind, label: string) => {
  if (kind === PriceSummaryItemKind.ClientPurchasingFee) {
    return (
      <Tooltip
        content={<div className={styles.serviceFeeTooltipContent}>{SERVICE_FEE_HELP_TEXT}</div>}
        isEnabled={true}
      >
        <span>
          {label}&nbsp;
          <Icon icon={faQuestionCircle} className={styles.serviceFeeTooltipIcon} />
        </span>
      </Tooltip>
    );
  }
  return label;
};

type OrderModificationItemProps = {
  orderItem: OrderModificationOrderItem;
};

const OrderModificationItem = ({ orderItem }: OrderModificationItemProps) => {
  const itemUrl = urls.products.detail({
    params: { id: orderItem.item.product.product_line.id },
    search: {
      item: orderItem.item.id,
      product: orderItem.item.product.id,
    },
  });

  const handleItemClick = () =>
    sendSelectItem(orderItem.item, "Order Modifications Detail", orderItem.unit_price);

  return (
    <Box>
      <div className={styles.item} key={orderItem.id}>
        <CatalogImage.Link
          dest={itemUrl}
          image={orderItem.item.image}
          size="thumbnail"
          alt={orderItem.item.full_name}
          className={styles.image}
          onClick={handleItemClick}
        />
        <div>
          <LinkButton dest={itemUrl} className={styles.link} onClick={handleItemClick}>
            {orderItem.item.product.product_line.name}
          </LinkButton>
          <div className={styles.subtitle}>
            {orderItem.item.product.name} · {orderItem.item.name}
            {orderItem.flavors_display && ` · ${orderItem.flavors_display}`} · Qty{" "}
            {orderItem.quantity}
          </div>
          {orderItem.patient && (
            <div className={styles.subtitle}>Ordered for {orderItem.patient.name_display}</div>
          )}
        </div>
      </div>
      <div className={cx(styles.info, styles.borderTop)}>
        {orderItem.modifications.map((mod) => (
          <div className={styles.description} key={mod}>
            <Icon icon={faInfoCircle} fixedWidth={true} className={styles.icon} />
            {mod}
          </div>
        ))}
        {!!orderItem.modification_notes && (
          <div className={styles.description}>
            <Icon icon={faQuoteLeft} fixedWidth={true} className={styles.icon} />
            <div className={styles.notes}>
              {orderItem.modification_notes}
              <span className={styles.from}>- Your veterinarian</span>
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

type OrderModificationsModalProps = {
  visible: boolean;
  onClose: () => void;
  orderId: number;
};
const OrderModificationsModal = ({ orderId, visible, onClose }: OrderModificationsModalProps) => {
  const {
    data: order,
    isLoading,
    error,
  } = useOrderModificationsQuery(orderId, {
    enabled: !!orderId,
  });

  const renderContent = () => {
    if (isLoading) {
      return <ModalLayout.Loading />;
    }

    if (error || !order) {
      return (
        <ModalLayout.FetchError error={error} entityName="order modifications" isPlural={true} />
      );
    }

    const newPriceSummary = getSummaryItems({
      ...order,
    });
    const oldPriceSummary = getSummaryItems({
      ...order.original_prices,
    });
    const priceSummary = newPriceSummary.map((item, idx) => ({
      ...item,
      oldValue: oldPriceSummary[idx],
    }));

    return (
      <ModalLayout.Content>
        <div className={styles.content}>
          {!!order?.order_items.length && (
            <div>
              <h2 className={styles.header}>Item Modifications</h2>
              <div className={styles.items}>
                {order?.order_items.map((orderItem) => (
                  <OrderModificationItem orderItem={orderItem} key={orderItem.id} />
                ))}
              </div>
            </div>
          )}
          {!!order?.modifications.length && (
            <div>
              <h2 className={styles.header}>Order Modifications</h2>
              <Box className={styles.info}>
                <div className={styles.notes}>
                  {order.modifications.map((mod) => (
                    <div className={styles.description} key={mod}>
                      <Icon icon={faInfoCircle} fixedWidth={true} className={styles.icon} />
                      {mod}
                    </div>
                  ))}
                </div>
                {!!order.modification_notes && (
                  <div className={styles.description}>
                    <Icon icon={faQuoteLeft} fixedWidth={true} className={styles.icon} />
                    <div className={styles.notes}>
                      {order.modification_notes}
                      <span className={styles.from}>- Your veterinarian</span>
                    </div>
                  </div>
                )}
              </Box>
            </div>
          )}
          <div>
            <h2 className={styles.header}>Price Summary</h2>
            <div className={styles.prices}>
              <div className={cx(styles.priceHeaders, styles.priceRow)}>
                <div />
                <span className={styles.value}>Original</span>
                <span className={styles.value}>Current</span>
              </div>
              {priceSummary.map(({ kind, label, value, oldValue }) => (
                <div
                  className={cx(
                    styles.priceRow,
                    kind === PriceSummaryItemKind.Total && styles.total,
                  )}
                  key={label}
                >
                  <span>{renderPriceLabel(kind, label)}</span>
                  <span className={styles.value}>{oldValue?.value}</span>
                  <span className={styles.value}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ModalLayout.Content>
    );
  };

  return (
    <Modal visible={visible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Order Modifications" onClose={onClose} />
        {renderContent()}
      </ModalLayout>
    </Modal>
  );
};

export default OrderModificationsModal;
